// Passkey (WebAuthn), fatia 1 — registro de uma passkey a partir da tela de Segurança.
// Progressive enhancement: sem WebAuthn no navegador, o botão desabilita e o aviso aparece.
// Same-origin (a RelyingParty usa o host efetivo), então a CSP (script_src :self) não muda.

function base64urlToBuffer (value) {
  const padding = '='.repeat((4 - (value.length % 4)) % 4)
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/')
  const binary = window.atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

function bufferToBase64url (buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i])
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export default class PasskeyButton extends HTMLElement {
  connectedCallback () {
    this.button = this.querySelector('button') || this.querySelector('[data-passkey-trigger]')
    this.errorEl = this.querySelector('[data-passkey-error]')

    if (!window.PublicKeyCredential) {
      this.setAttribute('data-unsupported', 'true')
      const form = this.querySelector('[data-passkey-form]')
      const unsupported = this.querySelector('[data-passkey-unsupported]')
      if (form) form.classList.add('hidden')
      if (unsupported) unsupported.classList.remove('hidden')
      return
    }

    if (this.button) {
      this.button.addEventListener('click', (event) => {
        event.preventDefault()
        this.register()
      })
    }
  }

  async register () {
    this.clearError()
    if (this.button) this.button.disabled = true

    try {
      const options = await this.fetchOptions()
      options.challenge = base64urlToBuffer(options.challenge)
      options.user.id = base64urlToBuffer(options.user.id)
      if (Array.isArray(options.excludeCredentials)) {
        options.excludeCredentials = options.excludeCredentials.map((cred) => ({
          ...cred,
          id: base64urlToBuffer(cred.id)
        }))
      }

      const credential = await navigator.credentials.create({ publicKey: options })
      const result = await this.postCredential(credential)

      if (result && result.redirect_url) {
        window.location.href = result.redirect_url
      } else {
        window.location.reload()
      }
    } catch (error) {
      if (this.button) this.button.disabled = false
      // Cancelamento pelo usuário ou passkey já existente: não é falha para alarmar.
      if (error && (error.name === 'NotAllowedError' || error.name === 'InvalidStateError')) return

      this.showError()
    }
  }

  async fetchOptions () {
    const response = await fetch(this.dataset.optionsUrl, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
    if (!response.ok) throw new Error('options request failed')
    return response.json()
  }

  async postCredential (credential) {
    const payload = {
      credential: {
        id: credential.id,
        type: credential.type,
        rawId: bufferToBase64url(credential.rawId),
        authenticatorAttachment: credential.authenticatorAttachment,
        clientExtensionResults: credential.getClientExtensionResults(),
        response: {
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
          attestationObject: bufferToBase64url(credential.response.attestationObject),
          transports: credential.response.getTransports ? credential.response.getTransports() : []
        }
      },
      nickname: this.nicknameValue()
    }

    const response = await fetch(this.dataset.createUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': this.csrfToken()
      },
      body: JSON.stringify(payload)
    })

    if (!response.ok) throw new Error('registration rejected')
    return response.json()
  }

  nicknameValue () {
    const input = this.querySelector('[data-passkey-nickname]')
    return input ? input.value.trim() : null
  }

  csrfToken () {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }

  clearError () {
    if (this.errorEl) this.errorEl.classList.add('hidden')
  }

  showError () {
    if (this.errorEl) this.errorEl.classList.remove('hidden')
  }
}
